import type React from "react";
import type { z } from "zod";

import type {
  ClaimBreakDownItem as ClaimBreakDownItemEntity,
  ClaimsVariation as ClaimsVariationEntity,
  TaskBreakDownItem,
  ClaimsApproved,
} from "./taskAndClaims.interfaces";
import type {
  ClaimBreakdownMainCostSchema as ClaimBreakdownMainCostSchemaType,
  PastClaimGroupSchema as PastClaimGroupSchemaType,
  PastClaimSchema as PastClaimSchemaType,
  PastClaimsSchemaArray as PastClaimsSchemaArrayType,
  TaskBreakdownMainCostSchema as TaskBreakdownMainCostSchemaType,
} from "./taskAndClaims.schema";

import type { NewOptions } from "@/app/app.interface";
import type {
  AllForOptions,
  AllOptions,
  NewEstimateOptionsData,
} from "@/app/app.types";
import type { ClaimsOption } from "@/features/projectRegister/projectRegister.types";
import type {
  Project as IProject,
  User,
} from "@/features/projects/projects.interfaces";
import type { SocketData } from "@/interfaces/globals";
import type { TEstimationData } from "@/interfaces/project-register.interface";

export enum TaskClaimsLabel {
  TASK_CLAIMS = "TASK And CLAIMS",
  FINALIZED_ESTIMATES = "Finalized Estimates",
  NEW_CLAIMS = "New Claims",
  DRAFTS_ESTIMATION = "Variations Estimation",
}

export enum TaskClaimStatus {
  ONGOING = "ONGOING",
  COMPLETED = "COMPLETED",
  CLOSED = "CLOSED",
}

export enum EstimateStatus {
  DRAFT = "DRAFT",
  AWAITING_FOR_APPROVAL = "AWAITING_FOR_APPROVAL",
  REJECTED = "REJECTED",
  APPROVED = "APPROVED",
  DID_NOT_PROCEED = "DID_NOT_PROCEED",
}

export enum ClaimSheetTabs {
  APPROVED = "APPROVED",
  VARIATION = "VARIATION",
  ESTIMATE = "ESTIMATE",
  DRIVE = "DRIVE",
}

export enum InvoiceStatus {
  READY_TO_INVOICE = "READY_TO_INVOICE",
  EMAIL_SENT = "EMAIL_SENT",
}

export enum TaskAndClaimsConstantsLocalKeys {
  SHEET_DATA = "task-and-claims-sheetdata",
  PROJECT_ID = "projectId",
  FOLDER_ID = "folderId",
}

export type WithSubItems = { subItems?: unknown[] };

export type ClaimBreakDownItem = ClaimBreakDownItemEntity;
export type TaskBreakDownData = TaskBreakDownItem;
export type TaskBreakdownMainCostFormData = z.infer<
  typeof TaskBreakdownMainCostSchemaType
>;
export type ClaimBreakdownMainCostFormData = z.infer<
  typeof ClaimBreakdownMainCostSchemaType
>;
export type PastClaimFormData = z.infer<typeof PastClaimSchemaType>;
export type PastClaimsFormDataArray = z.infer<typeof PastClaimsSchemaArrayType>;
export type PastClaimsSchemaArray = z.infer<typeof PastClaimsSchemaArrayType>;
export type PastClaimGroupSchema = z.infer<typeof PastClaimGroupSchemaType>;

export type ApprovedClaimsListMeta = {
  disableNewClaim?: boolean;
};

export type TaskClaimsData = {
  id?: string;
  projectId: string;
  leadsId: string;
  poNumber: string;
  poFile: string;
  amount: number;
  projectProgress: number;
  claimedProgressValue: number | null;
  claimProgress: number;
  wonDate: string;
  lastClaimedDate: string | null;
  comment: string | null;
  invoiceMethod: ClaimsOption;
  poStatusId: string;
  claimStatusId: string;
  project: IProject;
  estimatedBy: User;
  poStatus: ClaimsOption | null;
  status: TaskClaimStatus;
  draftVariantCount: number;
  approvedClaims: ClaimsApproved[];
};

export type TaskClaimsStatusMutationData = {
  status?: TaskClaimStatus;
  statusOverrideReason?: string;
};

export type GetClaimsProjectsRequest = {
  filter?: string;
  claimStatus?: TaskClaimStatus;
  customerId?: string;
};

export type TaskClaimsProjectFilterOption = Pick<
  TaskClaimsData["project"],
  | "id"
  | "name"
  | "status"
  | "lastActivityDate"
  | "customer"
  | "address"
  | "ourReferenceNumber"
  | "startDate"
>;

export type Drafts = TEstimationData & {
  isApproved: boolean;
  estimateStatus: EstimateStatus;
  fromClaims: boolean;
};

export type SpecialRateSocketData = SocketData & {
  customerId?: string;
  projectId?: string;
};

export type ClaimsVariation = ClaimsVariationEntity;

export type MutateClaims = {
  claimsId: string;
  claimsType: string;
  data?: Record<string, unknown>;
  showToast?: boolean;
  method: "PATCH" | "DELETE";
};

export type UpdateDetailData = {
  data: Record<string, unknown>;
  claimsId: string;
  claimsType: string;
  claimsDetailId: string;
  successMsg: string;
};

export type ApprovedOption = {
  value: string;
  id: string;
  statusFor: "poStatus" | "claimStatus" | "invoiceMethod" | "poNumber";
  claimsType: string;
  claimsDetailId?: string;
  successMsg: string;
};

export type TaskClaimsContextProps = {
  option: {
    isOpen: boolean;
    statusFor: string;
  };
  setOption: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      statusFor: string;
    }>
  >;
  isSheet: boolean;
  setSheet: React.Dispatch<React.SetStateAction<boolean>>;
  deleteOption: (idx: string) => Promise<void>;
  addOption: (data: NewOptions<AllForOptions>) => Promise<void>;
  editOption: (data: NewEstimateOptionsData) => Promise<void>;
  poStatusList: AllOptions[];
  invoiceMethodList: AllOptions[];
  statuses: AllOptions[];
  submissions: AllOptions[];
  optionMutate: (
    value: string,
    id: string,
    statusFor: "poStatus" | "claimStatus" | "invoiceMethod" | "poNumber",
  ) => Promise<void>;
  updatePoNumber: (value: string, id: string) => Promise<void>;
  updateWonDate: (value: string, id: string) => Promise<void>;
  updateEstimatedBy: (value: string, id: string) => Promise<void>;
  updatePoFile: (file: File | File[] | null, id: string) => Promise<void>;
  approvedOptionMutate: (options: ApprovedOption) => Promise<void>;
  updateDetailData: (options: UpdateDetailData) => Promise<void>;
  mutateClaimsInvoice: (options: MutateClaims) => Promise<void>;
  isLoadingClaimsDetails: boolean;
  handleEstimateMutation: (
    data: Record<string, unknown>,
    estimateId: string,
    msg: string,
  ) => Promise<void>;
  addEstimationOptions: (data: NewOptions<AllForOptions>) => Promise<void>;
  editEstimationOptions: (data: NewEstimateOptionsData) => Promise<void>;
};

export type TaskClaimsContextProviderProps = {
  children: React.ReactNode;
};

export type TaskSheetProps = {
  isOpen: boolean;
  itemId: string;
};

export type TaskBreakdownSheetProps = {
  sheet: TaskSheetProps;
  setSheet: React.Dispatch<React.SetStateAction<TaskSheetProps>>;
};

export type ApprovedSectionProps = {
  newClaim: {
    isOpen: boolean;
    type: string;
  };
  setNewClaim: React.Dispatch<
    React.SetStateAction<{ isOpen: boolean; type: string }>
  >;
  setShowAddClaimButton: React.Dispatch<React.SetStateAction<boolean>>;
};

export type ApprovedSection = ApprovedSectionProps;

export type ClaimsBreakdownSheetState = {
  isOpen: boolean;
  itemId: string;
  claimsIndex: number;
  isEdit?: boolean;
  invoiceId?: string;
  isPastClaims?: boolean;
};

export type SetClaimsBreakdownSheetState = React.Dispatch<
  React.SetStateAction<ClaimsBreakdownSheetState>
>;

export type TaskClaimsSessionData = {
  customerId?: string;
  projectId?: string;
  customerName?: string;
  leadsId?: string;
  projectName?: string;
  claimsId?: string;
  readPermission?: boolean;
  writePermission?: boolean;
};
